import React, { useRef } from 'react';
import { cn } from '@/lib/utils';
import { Phone, MessageSquare, Sparkles, SlidersHorizontal, LucideIcon } from 'lucide-react';

export type AgentTab = 'phone' | 'whatsapp' | 'behavior' | 'limits';

/**
 * Die vier Bereiche der Agenten-Seite als Karten statt als Reiter.
 * Der Radius (28px) ist derselbe wie die Ecke, an der die Seitenleiste in den
 * Inhaltsbereich uebergeht (Layout: rounded-tl-[28px]) — die Seite bleibt
 * dadurch in einer Formensprache.
 *
 * Jede Karte hat denselben Aufbau: Icon links oben, Statuszeile rechts oben,
 * Titel und eine Erklaerzeile unten. Dadurch liegen Titel und Metazeile ueber
 * alle vier Karten auf derselben Hoehe, egal wie viel oben rechts steht.
 */
const CARD_RADIUS = 'rounded-[28px]';

/** Markenblau der Seite — bewusst der einzige Farbakzent im Kartenraster. */
const ACCENT = '#1967D2';

type StatusTone = 'on' | 'off' | 'neutral';

/**
 * WhatsApp-Zustand des GEWAEHLTEN Agenten — nicht des Unternehmens.
 * - `connected`: unter dieser Nummer laeuft eine eigene WhatsApp-Anbindung.
 * - `inherited`: die Nummer hat keine eigene, es antwortet die Unternehmens-Anbindung.
 * - `disconnected`: es gibt gar keine WhatsApp-Anbindung.
 * - `unknown`: Status konnte nicht geladen werden (kein Zustand behaupten).
 */
export type WhatsAppAgentStatus = 'connected' | 'inherited' | 'disconnected' | 'unknown';

const WHATSAPP_STATUS_LABEL: Record<WhatsAppAgentStatus, { label: string; tone: StatusTone }> = {
  connected: { label: 'Verbunden', tone: 'on' },
  inherited: { label: 'Über Unternehmen', tone: 'neutral' },
  disconnected: { label: 'Getrennt', tone: 'off' },
  unknown: { label: 'Unbekannt', tone: 'neutral' },
};

interface AgentSectionCardsProps {
  active: AgentTab;
  onSelect: (tab: AgentTab) => void;
  /** Telefon-Webhook an/aus. */
  phoneActive: boolean;
  /** Ohne Twilio-Nummer kann der Telefonagent gar nicht laufen. */
  phoneAvailable: boolean;
  /** Anzeigename der gewaehlten Stimme. */
  voiceName?: string;
  /** WhatsApp-Anbindung des gewaehlten Agenten. */
  whatsappStatus: WhatsAppAgentStatus;
  /** Eigene WhatsApp-Begruessung hinterlegt statt Standardtext. */
  whatsappCustomGreeting: boolean;
}

interface SectionCard {
  id: AgentTab;
  title: string;
  icon: LucideIcon;
  /** Erklaerzeile unter dem Titel. */
  meta: string;
  /** Status-Pille oben rechts — nur wo es einen echten Zustand gibt. */
  status?: { label: string; tone: StatusTone };
  /**
   * Kanal-Marker oben rechts: diese Bereiche wirken auf Telefon UND WhatsApp
   * gleichzeitig, das zeigen zwei kleine Kanal-Icons.
   */
  channels?: boolean;
}

const AgentSectionCards: React.FC<AgentSectionCardsProps> = ({
  active,
  onSelect,
  phoneActive,
  phoneAvailable,
  voiceName,
  whatsappStatus,
  whatsappCustomGreeting,
}) => {
  const listRef = useRef<HTMLDivElement>(null);

  const cards: SectionCard[] = [
    {
      id: 'phone',
      title: 'Telefonagent',
      icon: Phone,
      meta: voiceName ? `Stimme · ${voiceName}` : 'Name, Begrüßung, Stimme',
      status: !phoneAvailable
        ? { label: 'Keine Nummer', tone: 'off' }
        : { label: phoneActive ? 'Aktiv' : 'Inaktiv', tone: phoneActive ? 'on' : 'off' },
    },
    {
      id: 'whatsapp',
      title: 'WhatsApp-Agent',
      icon: MessageSquare,
      meta: whatsappCustomGreeting ? 'Eigene Begrüßung hinterlegt' : 'Standardbegrüßung aktiv',
      status: WHATSAPP_STATUS_LABEL[whatsappStatus],
    },
    {
      id: 'behavior',
      title: 'Verhalten',
      icon: Sparkles,
      meta: 'Cross-Selling und freie Anweisungen',
      channels: true,
    },
    {
      id: 'limits',
      title: 'Limits',
      icon: SlidersHorizontal,
      meta: 'Budget, Blockliste, Vorwahlen',
      channels: true,
    },
  ];

  /** Pfeiltasten wandern durch die Karten, wie man es von Reitern kennt. */
  const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
    const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (step === 0) return;
    event.preventDefault();
    const next = (index + step + cards.length) % cards.length;
    onSelect(cards[next].id);
    listRef.current
      ?.querySelectorAll<HTMLButtonElement>('[role="tab"]')
      ?.[next]?.focus();
  };

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label="Agenten-Bereiche"
      className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4"
    >
      {cards.map((card, index) => {
        const isActive = active === card.id;
        const Icon = card.icon;

        return (
          <button
            key={card.id}
            role="tab"
            id={`agent-tab-${card.id}`}
            aria-selected={isActive}
            aria-controls={`agent-panel-${card.id}`}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onSelect(card.id)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            className={cn(
              CARD_RADIUS,
              'group relative isolate flex min-h-[168px] flex-col overflow-hidden border p-5 text-left',
              'transition-[transform,box-shadow,border-color,background-color] duration-300 [transition-timing-function:cubic-bezier(0.16,1,0.3,1)]',
              'focus:outline-none focus-visible:outline-none',
              'motion-reduce:transition-none',
              isActive
                ? 'border-transparent text-white shadow-[0_22px_45px_-22px_rgba(25,103,210,0.9)]'
                : 'border-border bg-card text-foreground shadow-sm hover:-translate-y-1 hover:border-[#1967D2]/40 hover:shadow-lg motion-reduce:hover:translate-y-0',
            )}
            style={isActive ? { backgroundColor: ACCENT } : undefined}
          >
            {/* Tiefe statt Flaeche: Lichtkegel oben links auf der aktiven Karte. */}
            {isActive && (
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 -z-10"
                style={{
                  background:
                    'radial-gradient(115% 85% at 14% 0%, rgba(255,255,255,0.26) 0%, rgba(255,255,255,0) 60%)',
                }}
              />
            )}

            {/* Wasserzeichen-Icon: gibt der Karte Ebene und Charakter. */}
            <Icon
              aria-hidden
              strokeWidth={1}
              className={cn(
                'pointer-events-none absolute -bottom-8 -right-7 -z-10 h-28 w-28 -rotate-12 transition-transform duration-500 [transition-timing-function:cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none',
                isActive
                  ? 'text-white opacity-[0.16]'
                  : 'text-foreground opacity-[0.05] group-hover:-translate-y-1 group-hover:-rotate-6 group-hover:opacity-[0.09]',
              )}
            />

            {/* Kopfzeile: feste Hoehe, damit Titel und Metazeile ueber alle
                vier Karten auf derselben Linie sitzen. */}
            <div className="flex h-10 items-center justify-between gap-2">
              <span
                className={cn(
                  'flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl transition-colors duration-300',
                  isActive
                    ? 'bg-white/20 text-white'
                    : 'bg-[#1967D2]/10 text-[#1967D2] group-hover:bg-[#1967D2]/15 dark:bg-primary/15 dark:text-primary-strong',
                )}
              >
                <Icon className="h-5 w-5" />
              </span>

              {card.status && (
                <span
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide',
                    isActive ? 'bg-white/15 text-white/90' : 'bg-muted text-muted-foreground',
                  )}
                >
                  <span
                    aria-hidden
                    className={cn(
                      'h-1.5 w-1.5 rounded-full',
                      isActive
                        ? 'bg-white/80'
                        : card.status.tone === 'on'
                          ? 'bg-success'
                          : card.status.tone === 'neutral'
                            ? 'bg-muted-foreground/60'
                            : 'bg-cancelled',
                    )}
                  />
                  {card.status.label}
                </span>
              )}

              {card.channels && (
                <span
                  className="inline-flex items-center gap-1"
                  title="Gilt für Telefon- und WhatsApp-Agent"
                >
                  <span className="sr-only">Gilt für Telefon- und WhatsApp-Agent</span>
                  {[Phone, MessageSquare].map((ChannelIcon, i) => (
                    <span
                      key={i}
                      aria-hidden
                      className={cn(
                        'flex h-6 w-6 items-center justify-center rounded-full border',
                        isActive
                          ? 'border-white/25 bg-white/10 text-white/90'
                          : 'border-border bg-muted/60 text-muted-foreground',
                      )}
                    >
                      <ChannelIcon className="h-3 w-3" />
                    </span>
                  ))}
                </span>
              )}
            </div>

            <div className="mt-auto pt-6">
              <h3
                className={cn(
                  'text-[15px] font-semibold leading-tight tracking-tight',
                  isActive ? 'text-white' : 'text-foreground',
                )}
              >
                {card.title}
              </h3>
              <p
                className={cn(
                  'mt-1.5 min-h-[2.25rem] text-xs leading-snug',
                  isActive ? 'text-white/75' : 'text-muted-foreground',
                )}
              >
                {card.meta}
              </p>
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default AgentSectionCards;
